import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useId, useRef } from 'react';

import useShiftActivity from '../hooks/useShiftActivity';

import ActivityItem from './ActivityItem';

// "Recent activity" modal, opened by View all in the Shift Detail panel
// (GRAVY-10294 — the link previously did nothing at all).
//
// Centred over the dashboard with a dimmed backdrop, so the panel and the
// calendar behind it are still visible and closing returns the admin to exactly
// the shift they were reading. It never navigates: nothing about the week,
// filters, selected shift or scroll position is touched by opening it.
//
// The panel's own Recent activity block is a preview capped by the API; this is
// where the complete, paginated history lives.

type Props = {
  agencyId: number;
  shiftId: number;
  /** Closes the modal and hands focus back to View all. */
  onClose: () => void;
};

const focusable = (root: HTMLElement | null): HTMLElement[] =>
  root
    ? Array.from(
        root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      )
    : [];

const RecentActivityModal = ({ agencyId, shiftId, onClose }: Props) => {
  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useShiftActivity(agencyId, shiftId, true);

  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Flattened across pages: Load more appends, so the admin never loses the
  // rows they have already read.
  const activities = (data?.pages ?? []).flatMap((page) => page.activities);
  const total = data?.pages?.[0]?.pagination.total ?? 0;

  // Escape closes the modal. The Shift Detail panel binds Escape on document
  // too, and stands down while this is open (see ShiftDetailPanel) — otherwise
  // one keypress would close both and dump the admin back on the calendar.
  //
  // Tab is trapped inside the dialog: this is a modal over another dialog, so
  // tabbing out would land on controls the backdrop has covered.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusable(dialogRef.current);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (
        e.shiftKey &&
        (active === first || !dialogRef.current?.contains(active))
      ) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Focus lands on the close button on open; the panel returns it to View all
  // on close, so the keyboard path in and out is symmetrical.
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const onLoadMore = useCallback(() => {
    if (!isFetchingNextPage) fetchNextPage();
  }, [fetchNextPage, isFetchingNextPage]);

  const body = () => {
    if (isLoading) {
      return (
        <div className="dashv2-actmodal-state" role="status">
          Loading activity…
        </div>
      );
    }

    if (isError) {
      return (
        <div className="dashv2-actmodal-state">
          <p>Unable to load activity.</p>
          <button
            type="button"
            className="dashv2-retry"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      );
    }

    if (activities.length === 0) {
      return (
        <div className="dashv2-actmodal-state">
          No activity recorded for this shift yet.
        </div>
      );
    }

    return (
      <>
        <div className="dashv2-panel-actlist">
          {activities.map((item) => (
            <ActivityItem key={item.id} item={item} />
          ))}
        </div>
        {hasNextPage && (
          <button
            type="button"
            className="dashv2-actmodal-more"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage
              ? 'Loading…'
              : `Load more (${activities.length} of ${total})`}
          </button>
        )}
      </>
    );
  };

  return (
    <>
      {/* Dims the dashboard and the Shift Detail panel rather than replacing
          them — the admin keeps sight of which shift they are reading. */}
      <div
        className="dashv2-actmodal-overlay"
        onClick={onClose}
        data-testid="dashv2-actmodal-overlay"
      />
      <div
        className="dashv2-actmodal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <div className="dashv2-actmodal-head">
          <h2 className="dashv2-actmodal-title" id={titleId}>
            Recent activity
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="dashv2-panel-close"
            aria-label="Close recent activity"
            onClick={onClose}
          >
            <XMarkIcon />
          </button>
        </div>

        <div className="dashv2-actmodal-body">{body()}</div>

        <div className="dashv2-actmodal-foot">
          <button
            type="button"
            className="dashv2-actmodal-back"
            onClick={onClose}
          >
            Go back
          </button>
        </div>
      </div>
    </>
  );
};

export default RecentActivityModal;
